import React, {useEffect, useRef, useState} from 'react'
import clsx from 'clsx'
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome'
import {faChevronDown} from '@fortawesome/free-solid-svg-icons'
import {booking} from '@whyhotel/common-schema'

export interface SearchSpecialRatesControlProps {
  specialRates: booking.SearchSpecialRatesParams
  onChange: (updatedParams: booking.SearchSpecialRatesParams) => void
  className?: string
}

interface FormState {
  hasPromoCode: boolean
  promoCode?: string
  hasCorporateCode: boolean
  corporateCode?: string
  hasAAA: boolean
  hasAARP: boolean
  hasGov: boolean
  hasMilitary: boolean
}

interface SpecialRateLabelProps {
  form: FormState
}

function SpecialRateLabel({form}: SpecialRateLabelProps): JSX.Element {
  const {
    hasCorporateCode,
    corporateCode,
    hasPromoCode,
    promoCode,
    hasAAA,
    hasAARP,
    hasGov,
    hasMilitary
  } = form
  if (hasPromoCode) {
    const displayPromoCode = promoCode
      ? promoCode?.length > 10
        ? `${promoCode.slice(0, 10)}...`
        : promoCode
      : 'None'
    return (
      <div className="flex flex-row items-center gap-1">
        <span>Promo Code:</span>
        <span
          className={clsx({
            'text-gray-500': promoCode && promoCode.length > 0,
            'text-error': !promoCode || promoCode.length === 0
          })}
        >
          {displayPromoCode}
        </span>
      </div>
    )
  } else if (hasCorporateCode) {
    const displayCorporateCode = corporateCode
      ? corporateCode?.length > 10
        ? `${corporateCode.slice(0, 10)}...`
        : corporateCode
      : 'None'
    return (
      <div className="flex flex-row items-center gap-1">
        <span>Corp Code:</span>
        <span
          className={clsx({
            'text-gray-500': corporateCode && corporateCode.length > 0,
            'text-error': !corporateCode || corporateCode.length === 0
          })}
        >
          {displayCorporateCode}
        </span>
      </div>
    )
  } else if (hasAAA) {
    return <span>AAA Rate</span>
  } else if (hasAARP) {
    return <span>AARP Rate</span>
  } else if (hasGov) {
    return <span>US Gov Rate</span>
  } else if (hasMilitary) {
    return <span>US Military Rate</span>
  } else {
    return <span>Add Promo/Corp Code</span>
  }
}

const SearchSpecialRatesControl = ({
  specialRates,
  onChange,
  className = ''
}: SearchSpecialRatesControlProps) => {
  const [isOpen, setIsOpen] = useState(false)
  const dropdownRef = useRef<HTMLDivElement>(null)

  const [formState, setFormState] = useState<FormState>({
    hasPromoCode: !!specialRates?.promoCode,
    promoCode: specialRates?.promoCode,
    hasCorporateCode: !!specialRates?.corporateCode,
    corporateCode: specialRates?.corporateCode,
    hasAAA: specialRates?.hasAAA || false,
    hasAARP: specialRates?.hasAARP || false,
    hasGov: specialRates?.hasGov || false,
    hasMilitary: specialRates?.hasMilitary || false
  })

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false)
      }
    }

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsOpen(false)
      }
    }

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside)
      document.addEventListener('keydown', handleEscape)
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
      document.removeEventListener('keydown', handleEscape)
    }
  }, [isOpen])

  // Sync with prop changes
  useEffect(() => {
    setFormState({
      hasPromoCode: !!specialRates?.promoCode,
      promoCode: specialRates?.promoCode,
      hasCorporateCode: !!specialRates?.corporateCode,
      corporateCode: specialRates?.corporateCode,
      hasAAA: specialRates?.hasAAA || false,
      hasAARP: specialRates?.hasAARP || false,
      hasGov: specialRates?.hasGov || false,
      hasMilitary: specialRates?.hasMilitary || false
    })
  }, [specialRates])

  const toggleDropdown = () => {
    setIsOpen(!isOpen)
  }

  return (
    <div ref={dropdownRef} className={clsx('w-full xl:min-w-52', className)}>
      {/* Dropdown Trigger Button */}
      <button
        type="button"
        onClick={toggleDropdown}
        className="flex flex-row items-center cursor-pointer py-2 pl-10 xl:pl-0 xl:py-1 w-full search-input"
        tabIndex={0}
        aria-label="Select special rates."
      >
        <div className="w-full text-left font-medium text-sm">
          <SpecialRateLabel form={formState} />
        </div>
        <FontAwesomeIcon
          icon={faChevronDown}
          className="ml-2 text-2xs"
          title="Toggle special rates field"
        />
      </button>

      {/* Dropdown Panel */}
      {isOpen && (
        <div
          className={clsx(
            'absolute z-[9999] w-64 mt-2 ',
            'bg-white border border-gray-200 rounded-md shadow-lg'
          )}
        >
          <div className="p-4 space-y-4">
            <div className="text-sm text-gray-600">
              <div className="flex flex-col gap-2">
                <div className="form-control">
                  <label className="label cursor-pointer justify-start gap-2">
                    <input
                      type="checkbox"
                      className="toggle toggle-sm toggle-accent"
                      checked={formState.hasPromoCode}
                      // onMouseDown={e => e.preventDefault()}
                      onChange={e => {
                        const updatedFormState = {
                          ...formState,
                          hasPromoCode: e.target.checked,
                          hasCorporateCode: false,
                          corporateCode: '',
                          hasAAA: false,
                          hasAARP: false,
                          hasGov: false,
                          hasMilitary: false
                        }
                        setFormState(updatedFormState)
                      }}
                      data-field="search-promocode-checkbox"
                      data-clarity-unmask="true"
                    />
                    <span className="label-text">Promo Code</span>
                  </label>
                </div>
                {formState.hasPromoCode && (
                  <div className="form-control">
                    <input
                      type="text"
                      className="input input-sm focus:ring-0 outline-none"
                      placeholder="Enter Promo Code"
                      value={formState.promoCode || ''}
                      onChange={e => {
                        const updatedFormState = {
                          ...formState,
                          hasPromoCode: true,
                          promoCode: e.target?.value?.trim(),
                          hasCorporateCode: false,
                          corporateCode: '',
                          hasAAA: false,
                          hasAARP: false,
                          hasGov: false,
                          hasMilitary: false
                        }
                        setFormState(updatedFormState)
                        onChange(updatedFormState)
                      }}
                      data-field="search-promocode-text"
                      data-clarity-unmask="true"
                    />
                  </div>
                )}
              </div>

              <div className="flex flex-col gap-2">
                <div className="form-control">
                  <label className="label cursor-pointer justify-start gap-2">
                    <input
                      type="checkbox"
                      className="toggle toggle-sm toggle-accent"
                      checked={formState.hasCorporateCode}
                      // onMouseDown={e => e.preventDefault()}
                      onChange={e => {
                        const updatedFormState = {
                          ...formState,
                          hasCorporateCode: e.target.checked,
                          hasPromoCode: false,
                          promoCode: '',
                          hasAAA: false,
                          hasAARP: false,
                          hasGov: false,
                          hasMilitary: false
                        }
                        setFormState(updatedFormState)
                      }}
                      data-field="search-corpcode-checkbox"
                      data-clarity-unmask="true"
                    />
                    <span className="label-text">Corporate Code</span>
                  </label>
                </div>
                {formState.hasCorporateCode && (
                  <div className="form-control">
                    <input
                      type="text"
                      className="input input-sm focus:ring-0 outline-none"
                      placeholder="Enter Corporate Code"
                      value={formState.corporateCode || ''}
                      onChange={e => {
                        const updatedFormState = {
                          ...formState,
                          corporateCode: e.target?.value?.trim(),
                          hasCorporateCode: true,
                          hasPromoCode: false,
                          promoCode: '',
                          hasAAA: false,
                          hasAARP: false,
                          hasGov: false,
                          hasMilitary: false
                        }
                        setFormState(updatedFormState)
                        onChange(updatedFormState)
                      }}
                      data-field="search-corpcode-text"
                      data-clarity-unmask="true"
                    />
                  </div>
                )}
              </div>

              <div className="form-control">
                <label className="label cursor-pointer justify-start gap-2">
                  <input
                    type="checkbox"
                    className="toggle toggle-sm toggle-accent"
                    checked={formState.hasAAA}
                    // onMouseDown={e => e.preventDefault()}
                    onChange={e => {
                      const updatedFormState = {
                        ...formState,
                        hasAAA: e.target.checked,
                        hasCorporateCode: false,
                        corporateCode: '',
                        hasPromoCode: false,
                        promoCode: '',
                        hasAARP: false,
                        hasGov: false,
                        hasMilitary: false
                      }
                      setFormState(updatedFormState)
                      onChange(updatedFormState)
                    }}
                    data-field="search-aaa-checkbox"
                    data-clarity-unmask="true"
                  />
                  <span className="label-text">AAA</span>
                </label>
              </div>
              <div className="form-control">
                <label className="label cursor-pointer justify-start gap-2">
                  <input
                    type="checkbox"
                    className="toggle toggle-sm toggle-accent"
                    checked={formState.hasAARP}
                    // onMouseDown={e => e.preventDefault()}
                    onChange={e => {
                      const updatedFormState = {
                        ...formState,
                        hasAARP: e.target.checked,
                        hasCorporateCode: false,
                        corporateCode: '',
                        hasPromoCode: false,
                        promoCode: '',
                        hasAAA: false,
                        hasGov: false,
                        hasMilitary: false
                      }
                      setFormState(updatedFormState)
                      onChange(updatedFormState)
                    }}
                    data-field="search-aarp-checkbox"
                    data-clarity-unmask="true"
                  />
                  <span className="label-text">AARP</span>
                </label>
              </div>
              <div className="form-control">
                <label className="label cursor-pointer justify-start gap-2">
                  <input
                    type="checkbox"
                    className="toggle toggle-sm toggle-accent"
                    checked={formState.hasGov}
                    // onMouseDown={e => e.preventDefault()}
                    onChange={e => {
                      const updatedFormState = {
                        ...formState,
                        hasGov: e.target.checked,
                        hasCorporateCode: false,
                        corporateCode: '',
                        hasPromoCode: false,
                        promoCode: '',
                        hasAAA: false,
                        hasAARP: false,
                        hasMilitary: false
                      }
                      setFormState(updatedFormState)
                      onChange(updatedFormState)
                    }}
                    data-field="search-usgov-checkbox"
                    data-clarity-unmask="true"
                  />
                  <span className="label-text">US Government</span>
                </label>
              </div>
              <div className="form-control">
                <label className="label cursor-pointer justify-start gap-2">
                  <input
                    type="checkbox"
                    className="toggle toggle-sm toggle-accent"
                    checked={formState.hasMilitary}
                    // onMouseDown={e => e.preventDefault()}
                    onChange={e => {
                      const updatedFormState = {
                        ...formState,
                        hasMilitary: e.target.checked,
                        hasCorporateCode: false,
                        corporateCode: '',
                        hasPromoCode: false,
                        promoCode: '',
                        hasAAA: false,
                        hasAARP: false,
                        hasGov: false
                      }
                      setFormState(updatedFormState)
                      onChange(updatedFormState)
                    }}
                    data-field="search-usmil-checkbox"
                    data-clarity-unmask="true"
                  />
                  <span className="label-text">US Military</span>
                </label>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default SearchSpecialRatesControl
